import { useCallback, useEffect, useMemo, useRef } from 'react'
import { trackEvent } from 'analytics/analytics'
import { setChertoExperiment } from 'actions/ChertoActions'
import { setOptimizelyExperiment } from 'actions/OptimizelyActions'
import config from 'constants/config'
import type { ExperimentConfig, ExperimentId } from 'constants/experiments'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getExperimentActivationEvent } from 'lib/experiments/experimentActivation'
import {
  isAllowedToTriggerExperiment,
  setTriggeredCookie,
} from 'lib/optimizely/optimizelyUtils'

const activatableExperimentSet = new Set([
  ...(config.OPTIMIZELY_EXPERIMENTS ?? []),
  ...(config.CHERTO_EXPERIMENTS ?? []),
])

function useExperimentActivation(
  experimentId: ExperimentId,
  canTrigger: boolean = true,
  overrideTrigger = false,
  experimentProvider: ExperimentConfig['provider'] = 'optimizely',
) {
  const dispatch = useAppDispatch()
  const hasTriggeredEvent = useRef(false)
  const previousVariationKey = useRef<string | undefined>(undefined)
  const utmContent = useAppSelector(state => state.utm.content)
  const isPostLoginExperimentSyncing = useAppSelector(state => state.optimizely.isPostLoginExperimentSyncing)

  const canTriggerExperiment = useMemo(() => {
    if (!activatableExperimentSet.has(experimentId)) {
      return false
    }
    if (isPostLoginExperimentSyncing) {
      return false
    }
    if (overrideTrigger) {
      return canTrigger
    }
    return isAllowedToTriggerExperiment(canTrigger, utmContent, experimentId)
  }, [canTrigger, isPostLoginExperimentSyncing, overrideTrigger, utmContent, experimentId])

  const experiment = useAppSelector(state => experimentProvider === 'cherto' ?
    state.cherto.chertoExperiments[experimentId] :
    state.optimizely.optimizelyExperiments[experimentId])

  const activate = useCallback(() => {
    const isActivatableExperiment = activatableExperimentSet.has(experimentId) && (
      overrideTrigger || isAllowedToTriggerExperiment(true, utmContent, experimentId)
    )
    if (isPostLoginExperimentSyncing || !isActivatableExperiment || hasTriggeredEvent.current || experiment?.triggered || !experiment) {
      return
    }

    const activationEvent = getExperimentActivationEvent(experimentId, experiment)
    if (activationEvent) {
      trackEvent(activationEvent)

      if (experiment.provider === 'cherto') {
        dispatch(setChertoExperiment(experimentId, experiment))
      } else {
        dispatch(setOptimizelyExperiment(experimentId, experiment))
      }
      setTriggeredCookie(experimentId, experiment.provider ?? 'optimizely')
      hasTriggeredEvent.current = true
    }
  }, [dispatch, experiment, experimentId, isPostLoginExperimentSyncing, overrideTrigger, utmContent])

  const currentVariationKey = experiment?.variationKey
  const experimentTriggered = experiment?.triggered
  useEffect(() => {
    if (currentVariationKey !== previousVariationKey.current) {
      if (hasTriggeredEvent.current && experimentTriggered === false) {
        hasTriggeredEvent.current = false
      }
      previousVariationKey.current = currentVariationKey
    }
  }, [currentVariationKey, experimentTriggered])

  useEffect(() => {
    if (canTriggerExperiment) {
      activate()
    }
  }, [activate, canTriggerExperiment])

  return activate
}

export default useExperimentActivation
